import { api } from '@common';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useAuth } from '@hooks';

// Interface for Product Document
export interface ProductDocument {
  id: string;
  productId: string;
  name: string;
  description?: string;
  fileUrl: string;
  createdAt: Date;
}

// Interface for Product Variant
export interface ProductVariant {
  id?: string;
  sku: string;
  size: string;
  price: number;
  stockQuantity: number;
}

// Interface for Product
export interface Product {
  id: string;
  name: string;
  description: string;
  imageUrl?: string | null;
  images?: string[];
  videoUrl?: string | null;
  sizes?: string | null;
  sizeStock?: Record<string, number> | null; // JSON object: { "S": 10, "M": 5, "L": 8, ... }
  category?: string | null;
  stockQuantity?: number;
  documents?: ProductDocument[];
  variants?: ProductVariant[];
  relatedProducts?: Product[];
  createdAt: Date;
  updatedAt: Date;
}

// Interface for products response (paginated)
interface ProductsResponse {
  data: Product[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

// Interface for Product Document metadata when creating/updating
export interface ProductDocumentMetadata {
  name: string;
  description?: string;
}

// Interface for creating/updating products
export interface CreateProductInput {
  name: string;
  description: string;
  imageUrl?: string;
  images?: File[];
  videoUrl?: string;
  category?: string;
  variants: Omit<ProductVariant, 'id'>[]; // Obrigatório e deve ter pelo menos 1 elemento
  documents?: File[];
  documentsMetadata?: ProductDocumentMetadata[];
}

export interface UpdateProductInput {
  name?: string;
  description?: string;
  imageUrl?: string;
  images?: File[];
  videoUrl?: string;
  category?: string;
  variants?: Omit<ProductVariant, 'id'>[];
  documents?: File[];
  documentsMetadata?: ProductDocumentMetadata[];
}

// Interface for product query parameters
export interface ProductQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
}

// Hook for fetching paginated products (for admin management)
export const getProducts = (params: ProductQueryParams) => {
  const { isAuthenticated } = useAuth();
  
  return useQuery<ProductsResponse>({
    queryKey: ['products', 'paginated', params],
    queryFn: async (): Promise<ProductsResponse> => {
      try {
        const response = await api.get<ProductsResponse>('/products', { params });
        
        if (response.data) {
          // Convert Decimal prices to numbers for frontend use
          const processedData = {
            ...response.data,
            data: response.data.data.map(product => ({
              ...product,
              imageUrl: product.imageUrl || undefined
            }))
          };
          return processedData;
        }
        
        return { data: [], pagination: { totalCount: 0, page: 1, pageSize: 10, totalPages: 0 } };
      } catch (error) {
        console.error('Error retrieving products:', error);
        message.error('Error loading products');
        throw error;
      }
    },
    enabled: isAuthenticated,
  });
};

export const useProducts = () => {
  const queryClient = useQueryClient();

  // Fetch all products (for public display)
  const products = useQuery<Product[], Error>({
    queryKey: ['products'],
    queryFn: async (): Promise<Product[]> => {
      try {
        // Get all products with a large limit
        const response = await api.get<ProductsResponse>('/products', {
          params: {
            limit: 100, // Get up to 100 products
            page: 1
          }
        });
        
        if (response.data && response.data.data) {
          // Convert Decimal price to number for frontend use
          return response.data.data.map(product => ({
            ...product,
            imageUrl: product.imageUrl || undefined
          }));
        }
        
        return [];
      } catch (error) {
        console.error('Error retrieving products:', error);
        message.error('Error loading products');
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Get a specific product by ID
  const getProduct = (id: string) => {
    return useQuery<Product>({
      queryKey: ['product', id],
      queryFn: async () => {
        try {
          const response = await api.get(`/products/${id}`);
          const product = response.data;
          return {
            ...product,
            imageUrl: product.imageUrl || undefined
          };
        } catch (error) {
          console.error(`Error fetching product ${id}:`, error);
          message.error('Unable to load product data');
          throw error;
        }
      },
      enabled: !!id,
    });
  };

  // Create a new product
  const addProduct = useMutation({
    mutationFn: async (productData: CreateProductInput) => {
      try {
        const formData = new FormData();
        
        // Add product fields to FormData
        formData.append('name', productData.name);
        formData.append('description', productData.description);
        
        if (productData.imageUrl) {
          formData.append('imageUrl', productData.imageUrl);
        }
        
        if (productData.videoUrl) {
          formData.append('videoUrl', productData.videoUrl);
        }
        
        if (productData.category) {
          formData.append('category', productData.category);
        }
        
        // Add variants (obrigatório - deve ter pelo menos 1)
        if (productData.variants && productData.variants.length > 0) {
          console.log('Sending variants to backend:', productData.variants);
          formData.append('variants', JSON.stringify(productData.variants));
        } else {
          console.error('No variants to send - this will cause validation error');
        }
        
        // Add multiple images
        if (productData.images && productData.images.length > 0) {
          productData.images.forEach((image) => {
            formData.append('images', image);
          });
        }
        
        // Add multiple documents
        if (productData.documents && productData.documents.length > 0) {
          productData.documents.forEach((document) => {
            formData.append('documents', document);
          });
        }
        
        // Add documents metadata as JSON
        if (productData.documentsMetadata && productData.documentsMetadata.length > 0) {
          formData.append('documentsMetadata', JSON.stringify(productData.documentsMetadata));
        }

        // Log FormData contents for debugging
        console.log('FormData contents:');
        for (let [key, value] of formData.entries()) {
          if (value instanceof File) {
            console.log(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
          } else {
            console.log(`  ${key}:`, value);
          }
        }

        const response = await api.post('/products', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error: any) {
        console.error('Error creating product:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        
        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Failed to create product: ${errorMsg}`);
        } else {
          message.error('Failed to create product');
        }
        
        throw error;
      }
    },
    onSuccess: () => {
      message.success('Product created successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  });

  // Update a product
  const updateProduct = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProductInput }) => {
      try {
        const formData = new FormData();
        
        // Add product fields to FormData (excluding File arrays and special fields)
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key !== 'images' && key !== 'documents' && key !== 'documentsMetadata' && key !== 'sizes' && key !== 'variants') {
              formData.append(key, value.toString());
            }
          }
        });
        
        // Handle special array fields
        if (data.variants && data.variants.length > 0) {
          formData.append('variants', JSON.stringify(data.variants));
        }
        
        // Add multiple images
        if (data.images && data.images.length > 0) {
          data.images.forEach((image) => {
            formData.append('images', image);
          });
        }
        
        // Add multiple documents
        if (data.documents && data.documents.length > 0) {
          data.documents.forEach((document) => {
            formData.append('documents', document);
          });
        }
        
        // Add documents metadata as JSON
        if (data.documentsMetadata && data.documentsMetadata.length > 0) {
          formData.append('documentsMetadata', JSON.stringify(data.documentsMetadata));
        }

        const response = await api.patch(`/products/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        console.error('API error updating product:', error);
        message.error('Error updating product');
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      message.success('Product updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
    }
  });

  // Delete a product
  const deleteProduct = useMutation({
    mutationFn: async (productId: string) => {
      try {
        const response = await api.delete(`/products/${productId}`);
        return response.data;
      } catch (error) {
        message.error('Error deleting product');
        throw error;
      }
    },
    onSuccess: (_, productId) => {
      message.success('Product deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
    }
  });

  return {
    products,
    getProduct,
    addProduct,
    updateProduct,
    deleteProduct,
  };
};

